import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// NOTE: adjust this path if your store file is "@/store" (index.ts) instead of "@/store/store".
import type { RootState } from "@/store";

export type OnchainDepositStatus = "idle" | "processing" | "settled";

export interface OnchainDepositState {
  status: OnchainDepositStatus;
  invoiceId: string | null;
  txHash: string | null;
  confirmations: number | null;
  // Epoch milliseconds when "processing" began. Used to rebuild the remaining
  // part of the 6-hour window after a page refresh.
  processingStartedAt: number | null;
}

const initialState: OnchainDepositState = {
  status: "idle",
  invoiceId: null,
  txHash: null,
  confirmations: null,
  processingStartedAt: null,
};

const onchainDepositSlice = createSlice({
  name: "onchainDeposit",
  initialState,
  reducers: {
    // On-chain deposit detected on the blockchain, waiting for confirmations.
    setOnchainDepositProcessing: (
      state,
      action: PayloadAction<{
        invoiceId: string;
        txHash?: string;
        confirmations?: number;
      }>,
    ) => {
      state.status = "processing";
      state.invoiceId = action.payload.invoiceId;
      state.txHash = action.payload.txHash ?? state.txHash;
      state.confirmations = action.payload.confirmations ?? state.confirmations;
      // Stamp the start time only ONCE. A second "processing" webhook (more
      // confirmations) must not restart the 6-hour window.
      if (state.processingStartedAt === null) {
        state.processingStartedAt = Date.now();
      }
    },
    // Optional: bump the confirmation count without resetting anything else.
    updateOnchainConfirmations: (
      state,
      action: PayloadAction<{ confirmations: number }>,
    ) => {
      state.confirmations = action.payload.confirmations;
    },
    setOnchainDepositSettled: (state) => {
      state.status = "settled";
    },
    resetOnchainDeposit: () => initialState,
  },
});

export const {
  setOnchainDepositProcessing,
  updateOnchainConfirmations,
  setOnchainDepositSettled,
  resetOnchainDeposit,
} = onchainDepositSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────
export const getOnchainDeposit = (state: RootState) => state.onchainDeposit;
export const getIsOnchainProcessing = (state: RootState) =>
  state.onchainDeposit.status === "processing";

export default onchainDepositSlice.reducer;
